import type { OfferData, StoreDto, CanonicalPricing } from '../../offer.types.ts';

export class CheckoutError extends Error {
  readonly #details: any;

  constructor(message: string, details?: any) {
    super(message);
    this.name = 'CheckoutError';
    this.#details = details;
  }

  get cause() {
    return this.#details?.cause;
  }

  get details() {
    return this.#details;
  }
}

// @ts-ignore
export enum DiscountTypeEnum {
  PERCENTAGE = 'percentage',
  FIXED = 'fixed',
}

// @ts-ignore
export enum DiscountUnitEnum {
  TOTAL = 'total',
  ITEM = 'items',
  SHIPPING = 'shipping',
}

export interface DiscountDto {
  type: DiscountTypeEnum;
  unit: DiscountUnitEnum;
  value: number;
}

export interface CouponDto {
  id: number;
  code: string;
  description?: string;
  active: boolean;
  startDate?: Date;
  endDate?: Date;
  availableTickets?: number;
  discountType: DiscountTypeEnum;
  discountUnit: DiscountUnitEnum;
  discountValue: number;
  store?: StoreDto;
}

export interface CustomerDto {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  documentNumber?: string;
  postcode?: string;
  addressStreet?: string;
  addressStreetNumber?: string;
  addressStreetComplement?: string;
  addressStreetDistrict?: string;
  addressCity?: string;
  addressState?: string;
  ip?: string;
  customText?: string;
  storeId: string;
  appmaxCustomerId?: number;
  appmaxLastUpdatedAt?: string;
  appmaxLastResponse?: Record<string, never>;
}

// @ts-ignore
export enum OrderStatusEnum {
  PENDING = 'pending',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}


export interface OrderItemDto {
  id?: number;
  name: string;
  notes?: string;
  sku?: string;
  totalPrice: number | string;
  unitPrice?: number | string;
  quantity: number;
  weight?: number;
  digital: boolean;
  productId: number;
  storeId: number;
}

export interface OrderDto {
  id?: number;
  /** ID público do pedido (nanoId / public id de 10 chars). Fonte canônica para tracking. */
  nanoId?: string;
  description?: string;
  status: OrderStatusEnum;
  coupon?: CouponDto;
  totalPrice: number | string;
  totalDiscounts: number | string;
  postcode?: string;
  addressStreet?: string;
  addressStreetNumber?: string;
  addressStreetComplement?: string;
  addressStreetDistrict?: string;
  addressCity?: string;
  addressState?: string;
  deliveryNote?: string;
  notes?: string;
  dataCheckoutRequest?: any;
  dataCheckoutResponse?: any;
  items: OrderItemDto[];
  offerId?: number;
  customerId: number;
  storeId: string;
  gatewayInfos?: {
    gatewayUsed?: string;
    gatewayId?: string;
    [key: string]: any;
  };
  integrationInfos?: any;
}

// @ts-ignore
export enum PaymentMethodEnum {
  CREDIT_CARD = 'credit_card',
  PIX = 'pix',
  BOLETO = 'boleto',
}

export interface CreditCardPaymentDto {
  // Credit card number
  cardNumber: string;

  // Card expiration date (MM/YYYY)',
  expirationDate: string;

  // Card security code (CVV/CVC)',
  securityCode: string;

  // Card holder name
  cardHolderName: string;

  // Card holder CPF',
  cardHolderDocumentNumber: string;

  // Number of installments',
  installments: number;

  // Last digits of the card
  lastDigits?: string;
}

export interface PixPaymentDto {
  // CPF for PIX payment (required if paymentMethod is pix)',
  cpf: string;
}

export interface BoletoPaymentDto {
  // CPF for Boleto payment (required if paymentMethod is boleto)',
  cpf: string;
}

export type PaymentDetailsUnion =
  | { paymentMethod: PaymentMethodEnum.CREDIT_CARD; creditCard: CreditCardPaymentDto }
  | { paymentMethod: PaymentMethodEnum.PIX; pix: PixPaymentDto }
  | { paymentMethod: PaymentMethodEnum.BOLETO; boleto: BoletoPaymentDto };

export interface CheckoutOrderDto {
  description?: string;
  coupon?: any;
  totalPrice: number | string;
  postcode?: string;
  addressStreet?: string;
  addressStreetNumber?: string;
  addressStreetComplement?: string;
  addressStreetDistrict?: string;
  addressCity?: string;
  addressState?: string;
  deliveryNote?: string;
  notes?: string;
}

// REMOVIDO: CheckoutRequestDto - não é mais usado (sistema antigo)

export interface CheckoutResponseDto {
  // Order data
  order: OrderDto;

  // Offer data
  offer: OfferData;

  // Customer data
  customer: CustomerDto;

  // Payment method
  paymentMethod: PaymentDetailsUnion;

  // Payment data - has different data for each payment method, like: qrcode for pix, PDF url for boleto, etc
  paymentData: any;

  // Payment status
  paymentStatus: string;

  // Checkout error
  checkoutError?: any;
}

export interface ApplyCouponRequestDto {
  coupons: string[];
  storeId: string;
  offerNanoId: string;
  paymentMethod?: 'credit_card' | 'pix' | 'boleto';
}

export interface ApplyCouponResponseDto {
  coupons: CouponDto[];
  discountAboutTotalValue: string;
  discountAboutProducts: string;
  discountAboutFreight: string;
}

// ===== TIPOS UNIFICADOS - NOVA ESTRUTURA =====

export interface UnifiedCustomerData {
  fullName: string;
  email: string;
  phone: string;
  document?: string;
  birth?: string;
}

export interface UnifiedShippingData {
  zipCode: string;
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
}

export interface UnifiedPaymentData {
  method: 'credit_card' | 'pix' | 'boleto';
  document: string;
  
  creditCard?: {
    number: string;
    holderName: string;
    expiryDate: string;
    cvv: string;
    installments: string;
    cpf?: string;
  };
}

export interface UnifiedPricingData {
  subtotal: number;
  shipping: number;
  discounts: number;
  total: number;
}

export interface UnifiedItemData {
  variant_id: number;
  name: string;
  quantity: number;
  price: number;
  properties?: Record<string, any>;
}

export interface UnifiedCheckoutRequestDto {
  offerNanoId: string;
  /** Quando definido, o backend monta customer, shipping, coupons e items (oferta + carrinho). */
  cartCode?: string;
  customer?: UnifiedCustomerData;
  shipping?: UnifiedShippingData;
  payment: UnifiedPaymentData;
  coupons?: string[];
  items?: UnifiedItemData[];
  // pricing removido - backend calcula automaticamente
}

// ===== TIPOS PARA PROCESSAMENTO EM CAMADAS =====

export interface GatewayResponse {
  success: boolean | 'pending_3ds' | 'pending_analysis' | 'pending_payment' | 'payment_in_process' | 'card_denied';
  orderId: string;
  paymentData?: any;
  transactionId?: string; // Campo para ID do gateway
  error?: string;
  requiresAdditionalSteps?: boolean; // Adicionado campo que estava faltando
}

export interface IntegrationResponse {
  success: boolean;
  orderId: string;
  platformOrderId?: string;
  error?: string;
}

export interface AppResponse {
  success: boolean;
  orderId: string;
  appOrderId?: string;
  error?: string;
}

// ===== TIPO DE RESPOSTA UNIFICADA =====

export interface UnifiedCheckoutResponseDto {
  success: boolean | 'pending_3ds' | 'pending_analysis' | 'pending_payment' | 'payment_in_process' | 'card_denied';
  orderId?: string;
  /** Token de posse da sessão 3DS/Stripe diferida — guardar e devolver em processAdditionalPayment. */
  sessionToken?: string;
  transactionId?: string;
  integrationOrderId?: string;
  gatewayUsed?: string;
  checkoutToken?: string;
  paymentData?: any;
  error?: string;
  message?: string;
  requiresAdditionalSteps?: boolean;
  finalPricing?: {
    total: number;
    totalDiscounts: number;
    couponDiscount?: number;
    paymentMethodDiscount?: number;
  };
  /** Pricing canônico (Etapa 2). Quando presente, preferir sobre finalPricing. */
  pricing?: CanonicalPricing;
}

export interface ThankYouByTokenResponseDto {
  token: string;
  orderId: string;
  transactionId?: string;
  paymentMethod: 'credit_card' | 'pix' | 'boleto' | string;
  paymentStatus: 'success' | 'pending' | string;
  paymentData?: any;
  offerNanoId: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerDocument?: string;
  totalPrice: number;
  totalDiscounts: number;
}

// ===== TIPOS PARA 3DS =====

export interface ThreeDSData {
  action: '3ds_complete';
  latamId: string;
  paymentData: {
    address: Record<string, any>;
    credit_card: Record<string, any>;
    credentials: Record<string, any>;
  };
}

export interface PaymentError {
  code: string;
  message: string;
  details?: any;
}